import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { User, X } from "lucide-react";
import type { FiscalDocumentDto } from "@finora/types";
import { api } from "../../lib/api";
import { CustomerAutocomplete } from "../../components/CustomerAutocomplete";

export const BUYER_ID_TYPES: { value: string; label: string }[] = [
  { value: "10", label: "10: PIB - domaće pravno lice" },
  { value: "11", label: "11: JMBG - fiz. lice, samostalna delatnost" },
  { value: "12", label: "12: PIB i JBKJS (PIB:JBKJS)" },
  { value: "13", label: "13: Kod penzionerske kartice" },
  { value: "14", label: "14: PIB - poljoprivredno gazdinstvo" },
  { value: "15", label: "15: JMBG - poljoprivredno gazdinstvo" },
  { value: "16", label: "16: BPG - broj poljoprivrednog gazdinstva" },
  { value: "20", label: "20: Broj lične karte - domaće fiz. lice" },
  { value: "21", label: "21: Broj izbegličke legitimacije" },
  { value: "22", label: "22: EBS - strano fiz. lice sa boravkom u Srbiji" },
  { value: "23", label: "23: Broj pasoša - domaće fiz. lice" },
  { value: "30", label: "30: Broj pasoša - strano fiz. lice" },
  { value: "31", label: "31: Broj diplomatske legitimacije/LK" },
  { value: "32", label: "32: Lična karta MKD (Makedonija)" },
  { value: "33", label: "33: Lična karta MNE (Crna Gora)" },
  { value: "34", label: "34: Lična karta ALB (Albanija)" },
  { value: "35", label: "35: Lična karta BIH" },
  { value: "36", label: "36: Lična karta - EU/Švajcarska/Norveška/Island" },
  { value: "40", label: "40: Poreski ID iz strane države (TIN)" },
];

// Kratka labela za polje "Vrednost" po izabranom tipu (razgovor 26.09.2026 -
// "ako pise samo JMBG stavis JMBG polje"). Tip "12" se NE nalazi ovde -
// renderuje se posebno kao DVA odvojena polja (PIB + JBKJS), vidi ispod.
export const ID_TYPE_FIELD_LABELS: Record<string, string> = {
  "10": "PIB",
  "11": "JMBG",
  "13": "Kod penzionerske kartice",
  "14": "PIB",
  "15": "JMBG",
  "16": "BPG",
  "20": "Broj lične karte",
  "21": "Broj izbegličke legitimacije",
  "22": "EBS",
  "23": "Broj pasoša",
  "30": "Broj pasoša",
  "31": "Broj diplomatske legitimacije/LK",
  "32": "Lična karta (MKD)",
  "33": "Lična karta (MNE)",
  "34": "Lična karta (ALB)",
  "35": "Lična karta (BIH)",
  "36": "Lična karta",
  "40": "TIN (poreski ID)",
};

// Opciono polje kupca (buyerCostCenterId) po šifarniku Poreske uprave; "50" (korporacijska kartica)
// važi samo uz plaćanje VAUČEROM, pa nije ponuđeno.
const OPTIONAL_BUYER_TYPES = [
  { value: "20", label: "Broj SNPDV" },
  { value: "21", label: "Broj LNPDV" },
  { value: "30", label: "Broj PPO-PDV" },
  { value: "31", label: "Broj ZPPO-PDV" },
  { value: "32", label: "Broj MPPO-PDV" },
  { value: "33", label: "Broj IPPO-PDV" },
];

// Tipovi kod kojih polje zaista predstavlja PIB (9 cifara) - samo za njih se
// pokušava auto-popuna iz šifarnika kad unos dostigne tačnu dužinu PIB-a
// (razgovor 26.09.2026: "ako se rucno unese i nadje u bazi automatski
// popuni Naziv firme", i "ne na svaki stop, uslov 9 karaktera").
const PIB_LOOKUP_TYPES = new Set(["10", "12", "14"]);
const PIB_LENGTH = 9;

export function BuyerSection({ document: doc }: { document: FiscalDocumentDto | null }) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [manualPib, setManualPib] = useState("");
  // Samo za tip "12" (PIB i JBKJS) - Poreska uprava zahteva OBA podatka,
  // spojena kao "PIB:JBKJS" u polju koje se šalje PFR-u (razgovor
  // 26.09.2026: "oba da budu napisana").
  const [manualJbkjs, setManualJbkjs] = useState("");
  const [manualName, setManualName] = useState("");
  const [manualAddress, setManualAddress] = useState("");
  const [optionalType, setOptionalType] = useState("20");
  const [optionalValue, setOptionalValue] = useState("");
  const [manualIdType, setManualIdType] = useState("10");
  const [pibLookupPending, setPibLookupPending] = useState(false);

  const update = useMutation({
    mutationFn: (payload: {
      customerId?: string | null;
      buyerPib?: string | null;
      buyerName?: string | null;
      buyerIdType?: string | null;
      buyerAddress?: string | null;
      buyerOptionalField?: string | null;
    }) => api.updateFiscalDocument(doc!.id, payload),
    onSuccess: (updated) => {
      queryClient.setQueryData(["fiscal-document", updated.id], updated);
      setExpanded(false);
    },
  });

  if (!doc) return null;

  const hasBuyer = doc.customerId || doc.buyerPib || doc.buyerName;

  if (hasBuyer) {
    const displayAddress = doc.customer?.address ?? doc.buyerAddress ?? null;
    return (
      <div className="mb-3 flex items-center justify-between rounded-lg bg-app-bg px-3 py-2 text-sm">
        <span className="flex flex-col gap-0.5 text-slate-700">
          <span className="flex items-center gap-1.5">
            <User className="h-3.5 w-3.5 text-slate-400" aria-hidden />
            {doc.customer?.name ?? doc.buyerName ?? "—"}
            {(doc.customer?.taxId ?? doc.buyerPib) && (
              <span className="text-xs text-slate-400">
                {" "}
                · {doc.customer ? doc.customer.buyerIdType ?? "10" : doc.buyerIdType ?? "10"}:
                {doc.customer?.taxId ?? doc.buyerPib}
              </span>
            )}
          </span>
          {displayAddress && <span className="pl-5 text-xs text-slate-400">{displayAddress}</span>}
          {doc.buyerOptionalField && <span className="pl-5 text-xs text-slate-400">Opciono polje: {doc.buyerOptionalField}</span>}
        </span>
        <button
          onClick={() =>
            update.mutate({
              customerId: null,
              buyerPib: null,
              buyerName: null,
              buyerIdType: null,
              buyerAddress: null,
              buyerOptionalField: null,
            })
          }
          className="text-slate-400 hover:text-red-600"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
    );
  }

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="mb-3 flex items-center gap-1.5 text-xs text-brand hover:underline"
      >
        <User className="h-3.5 w-3.5" aria-hidden />
        Dodaj kupca na račun (opciono)
      </button>
    );
  }

  const isSplitPibJbkjs = manualIdType === "12";
  const valueFieldLabel = ID_TYPE_FIELD_LABELS[manualIdType] ?? "Vrednost";

  /**
   * Kad PIB (tip 10/12/14) dostigne tačnu dužinu od 9 cifara, proveri da li
   * takav kupac VEĆ POSTOJI u šifarniku - ako je pronađen TAČNO JEDAN
   * (pun poklop taxId-a, ne samo "sadrži"), automatski se vezuje kao pravi
   * `customerId` (čistije za istoriju/izveštaje nego duplirati ručni unos),
   * umesto samo da mu se popuni naziv. Ako nema poklapanja ili ih ima više,
   * ručni unos ostaje nepromenjen - kasir nastavlja kao do sada.
   */
  async function handlePibChange(value: string) {
    setManualPib(value);
    if (!PIB_LOOKUP_TYPES.has(manualIdType) || value.length !== PIB_LENGTH || !/^\d{9}$/.test(value)) {
      return;
    }
    setPibLookupPending(true);
    try {
      const results = await api.listCustomers(value);
      const exactMatches = results.filter((c) => c.taxId === value);
      const [onlyMatch] = exactMatches;
      if (exactMatches.length === 1 && onlyMatch) {
        update.mutate({ customerId: onlyMatch.id });
      }
    } catch {
      // Tiha greška - pretraga je samo pogodnost, ne sme blokirati ručni unos.
    } finally {
      setPibLookupPending(false);
    }
  }

  const canSave = isSplitPibJbkjs
    ? Boolean(manualPib && manualJbkjs)
    : Boolean(manualPib || manualName);

  return (
    <div className="mb-3 space-y-2 rounded-lg border border-border p-3">
      <p className="text-xs font-medium text-slate-500">Kupac iz šifarnika</p>
      <CustomerAutocomplete
        value=""
        placeholder="Kucajte naziv kupca..."
        onSelect={(customer) => update.mutate({ customerId: customer.id })}
      />
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <div className="h-px flex-1 bg-border" /> ili unesite ručno <div className="h-px flex-1 bg-border" />
      </div>
      <div className="flex gap-2">
        {isSplitPibJbkjs ? (
          <>
            <input
              value={manualPib}
              onChange={(e) => void handlePibChange(e.target.value)}
              placeholder="PIB"
              className="h-9 flex-1 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            />
            <input
              value={manualJbkjs}
              onChange={(e) => setManualJbkjs(e.target.value)}
              placeholder="JBKJS"
              className="h-9 flex-1 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            />
          </>
        ) : (
          <input
            value={manualPib}
            onChange={(e) => void handlePibChange(e.target.value)}
            placeholder={valueFieldLabel}
            className="h-9 flex-1 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
        )}
        <input
          value={manualName}
          onChange={(e) => setManualName(e.target.value)}
          placeholder="Naziv firme"
          className="h-9 flex-1 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
        />
      </div>
      {pibLookupPending && <p className="text-xs text-slate-400">Provera šifarnika...</p>}
      <input
        value={manualAddress}
        onChange={(e) => setManualAddress(e.target.value)}
        placeholder="Adresa"
        className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
      />
      <div>
        <label className="mb-1 block text-xs text-slate-400">Opciono polje kupca (samo Promet Prodaja, neobavezno)</label>
        <div className="flex gap-2">
          <select
            value={optionalType}
            onChange={(e) => setOptionalType(e.target.value)}
            className="h-9 w-56 rounded-lg border border-border px-2 text-xs focus:border-brand focus:outline-none"
          >
            {OPTIONAL_BUYER_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.value}: {t.label}
              </option>
            ))}
          </select>
          <input
            value={optionalValue}
            onChange={(e) => setOptionalValue(e.target.value)}
            placeholder="Broj"
            className="h-9 flex-1 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
        </div>
      </div>
      <div>
        <label className="mb-1 block text-xs text-slate-400">
          Tip identifikacije (polje ID kupca na fiskalnom računu)
        </label>
        <select
          value={manualIdType}
          onChange={(e) => {
            setManualIdType(e.target.value);
            if (e.target.value !== "12") setManualJbkjs("");
          }}
          className="h-9 w-full rounded-lg border border-border px-2 text-xs focus:border-brand focus:outline-none"
        >
          {BUYER_ID_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={() => setExpanded(false)} className="text-xs text-slate-400 hover:text-slate-600">
          Otkaži
        </button>
        <button
          onClick={() =>
            update.mutate({
              buyerPib: isSplitPibJbkjs ? `${manualPib}:${manualJbkjs}` : manualPib,
              buyerName: manualName,
              buyerIdType: manualIdType,
              buyerAddress: manualAddress || null,
              buyerOptionalField: optionalValue.trim() ? `${optionalType}:${optionalValue.trim()}` : null,
            })
          }
          disabled={!canSave}
          className="rounded-lg bg-brand px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
        >
          Sačuvaj
        </button>
      </div>
    </div>
  );
}
